import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useSessionTracking } from './useSessionTracking';
import { toast } from 'sonner';

interface LikeData {
  id: string;
  user_id: string;
  session_key: string | null;
  likeable_type: 'post' | 'comment';
  likeable_id: string;
  created_at: string;
}

export const useLikes = (likeableId: string, likeableType: 'post' | 'comment') => {
  const { user } = useAuth();
  const { sessionKey } = useSessionTracking();
  const [likes, setLikes] = useState<LikeData[]>([]);
  const [likeCount, setLikeCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchLikes = async () => {
    try {
      const { data, error } = await supabase
        .from('likes')
        .select('*')
        .eq('likeable_id', likeableId)
        .eq('likeable_type', likeableType);

      if (error) throw error;

      const typedData = (data || []).map(item => ({
        ...item,
        likeable_type: item.likeable_type as 'post' | 'comment'
      }));
      
      setLikes(typedData);
      setLikeCount(typedData.length);
      // Check if liked by user_id (if logged in) OR by session_key (if not logged in or pending association)
      const hasLike = user 
        ? typedData.some(like => like.user_id === user.id || like.session_key === sessionKey)
        : typedData.some(like => like.session_key === sessionKey);
      setIsLiked(hasLike);
    } catch (error: any) {
      console.error('Error fetching likes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLikes();
  }, [likeableId, likeableType, user, sessionKey]);

  const toggleLike = async (overrideSessionKey?: string) => {
    const effectiveSessionKey = overrideSessionKey || sessionKey;
    
    // Allow anonymous likes with session key
    if (!user && !effectiveSessionKey) {
      toast.error('Unable to track like');
      return;
    }

    try {
      if (isLiked) {
        // Unlike
        const deleteQuery = supabase
          .from('likes')
          .delete()
          .eq('likeable_id', likeableId)
          .eq('likeable_type', likeableType);

        if (user) {
          // Delete by user_id OR session_key to handle pending associations
          deleteQuery.or(`user_id.eq.${user.id},session_key.eq.${effectiveSessionKey}`);
        } else if (effectiveSessionKey) {
          deleteQuery.eq('session_key', effectiveSessionKey);
        }

        const { error } = await deleteQuery;
        if (error) throw error;
        
        setIsLiked(false);
        setLikeCount(prev => prev - 1);
      } else {
        // Like
        const { error } = await supabase
          .from('likes')
          .insert({
            user_id: user?.id || null,
            session_key: effectiveSessionKey || null,
            likeable_id: likeableId,
            likeable_type: likeableType,
            association_status: 'pending'
          });

        if (error) throw error;
        
        setIsLiked(true);
        setLikeCount(prev => prev + 1);
      }
    } catch (error: any) {
      console.error('Error toggling like:', error);
      toast.error('Failed to update like');
    }
  };

  return {
    likes,
    likeCount,
    isLiked,
    loading,
    toggleLike
  };
};